/**
 * Aba Segurança & MFA (C2) — fiel ao mockup.
 * - Alterar senha: reusa AuthContext.changePassword (/api/auth/change-password).
 * - MFA: reusa MfaEnrollmentPanel + api de /api/me/mfa, disable, regenerar.
 * - Sessões ativas (B2 novo): lista + revogar individual + encerrar outras.
 */
import React, { useCallback, useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import MfaEnrollmentPanel from "../../../components/MfaEnrollmentPanel";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  fetchMfaStatus,
  postMfaDisable,
  postMfaRegenerarCodigos,
} from "../../Acessos/api";
import {
  getSessoes,
  revogarOutrasSessoes,
  revogarSessao,
} from "../../../services/configuracoes";

function fmtData(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return null;
  }
}

function tempoRelativo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 2) return "agora";
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return d.toLocaleDateString("pt-BR");
}

export default function SegurancaTab() {
  const { request, changePassword } = useAuth();
  const toast = useToast();

  // senha
  const [senha, setSenha] = useState({ senha_atual: "", nova_senha: "", confirmar_nova_senha: "" });

  // mfa
  const [mfa, setMfa] = useState(null);
  const [mfaPanelOpen, setMfaPanelOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableForm, setDisableForm] = useState({ senha: "", codigo: "" });
  const [disableErr, setDisableErr] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenCodigo, setRegenCodigo] = useState("");
  const [regenErr, setRegenErr] = useState(null);
  const [novosCodigos, setNovosCodigos] = useState(null);

  // sessoes
  const [sessoes, setSessoes] = useState([]);

  const carregarMfa = useCallback(async () => {
    try {
      const resp = await fetchMfaStatus(request);
      setMfa(resp?.data || resp || null);
    } catch {
      setMfa(null);
    }
  }, [request]);

  const carregarSessoes = useCallback(async () => {
    try {
      const data = await getSessoes(request);
      setSessoes(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setSessoes([]);
    }
  }, [request]);

  useEffect(() => {
    carregarMfa();
    carregarSessoes();
  }, [carregarMfa, carregarSessoes]);

  const mfaAtivo = Boolean(mfa?.ativo ?? mfa?.mfa_ativo ?? mfa?.habilitado);
  const mfaDesde = fmtData(mfa?.ativado_em);
  const recovery = mfa?.codigos_recuperacao_restantes ?? mfa?.recovery_restantes ?? mfa?.codigos_restantes;

  async function handleDisable(e) {
    e.preventDefault();
    setDisableErr(null);
    try {
      await postMfaDisable(request, disableForm.senha, disableForm.codigo);
      setDisableOpen(false);
      setDisableForm({ senha: "", codigo: "" });
      toast.success("MFA desativado.");
      await carregarMfa();
    } catch (err) {
      setDisableErr(err?.message || "Falha ao desativar MFA.");
    }
  }

  async function handleRegen(e) {
    e.preventDefault();
    setRegenErr(null);
    try {
      const resp = await postMfaRegenerarCodigos(request, regenCodigo);
      const data = resp?.data || resp;
      setNovosCodigos(data?.recovery_codes || data?.codigos || []);
      setRegenCodigo("");
      await carregarMfa();
    } catch (err) {
      setRegenErr(err?.message || "Código inválido.");
    }
  }

  async function encerrarOutras() {
    await revogarOutrasSessoes(request);
    await carregarSessoes();
  }

  async function encerrarUma(id) {
    try {
      await revogarSessao(request, id);
      await carregarSessoes();
      toast.success("Sessão encerrada.");
    } catch (err) {
      toast.error(err?.message || "Falha ao encerrar sessão.");
    }
  }

  const temOutras = sessoes.filter((s) => !s.atual).length > 0;

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Segurança &amp; MFA</h2>
        <p className="set-section__desc">Altere sua senha e gerencie a autenticação em dois fatores.</p>
      </div>
      <div className="set-section__body">
        {/* Alterar senha */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Alterar senha</div>
            <div className="set-row__desc">Use ao menos 8 caracteres com letras, números e símbolos.</div>
          </div>
          <div className="set-row__control">
            <div className="input-group">
              <label className="input-label">Senha atual</label>
              <input className="input" type="password" value={senha.senha_atual} onChange={(e) => setSenha((s) => ({ ...s, senha_atual: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Nova senha</label>
              <input className="input" type="password" placeholder="Nova senha" value={senha.nova_senha} onChange={(e) => setSenha((s) => ({ ...s, nova_senha: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Confirmar nova senha</label>
              <input className="input" type="password" placeholder="Repita a nova senha" value={senha.confirmar_nova_senha} onChange={(e) => setSenha((s) => ({ ...s, confirmar_nova_senha: e.target.value }))} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <AsyncButton
                variant="primary"
                onClick={async () => {
                  await changePassword(senha);
                  setSenha({ senha_atual: "", nova_senha: "", confirmar_nova_senha: "" });
                }}
                successMessage="Senha alterada."
              >
                Alterar senha
              </AsyncButton>
            </div>
          </div>
        </div>

        {/* MFA */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Autenticação em 2 etapas (MFA)</div>
            <div className="set-row__desc">Camada extra de segurança via app autenticador (TOTP).</div>
          </div>
          <div className="set-row__control">
            {mfaAtivo ? (
              <>
                <div style={{ padding: "14px 16px", background: "var(--success-bg)", border: "1px solid var(--success-border)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center", gap: 12 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--success-solid)" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>
                  <div style={{ fontSize: 13, color: "var(--success-text)", lineHeight: 1.4 }}>
                    <strong>MFA ativo{mfaDesde ? ` desde ${mfaDesde}` : ""}.</strong>
                    {recovery != null ? <><br />{recovery} código(s) de recuperação restante(s).</> : null}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setNovosCodigos(null); setRegenOpen(true); }}>Gerar novos códigos</button>
                  <button className="btn btn--ghost btn--sm" type="button" style={{ color: "var(--danger-text)" }} onClick={() => setDisableOpen(true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg> Desativar MFA
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ padding: "14px 16px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: 13, color: "var(--ink-2)" }}>
                  Sua conta ainda não tem MFA ativo. Adicione um segundo fator para mais segurança.
                </div>
                <button className="btn btn--primary btn--sm" type="button" style={{ alignSelf: "flex-start" }} onClick={() => setMfaPanelOpen(true)}>Configurar MFA</button>
              </>
            )}
          </div>
        </div>

        {/* Sessões ativas */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Sessões ativas</div>
            <div className="set-row__desc">Dispositivos onde sua conta está conectada.</div>
          </div>
          <div className="set-row__control">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sessoes.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--ink-3)" }}>Nenhuma sessão ativa encontrada.</div>
              ) : (
                sessoes.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                      background: s.atual ? "var(--accent-soft)" : "var(--surface-2)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: s.atual ? "var(--accent)" : "var(--ink)" }}>
                      {s.device_label}{s.atual ? " (atual)" : ""}
                    </div>
                    <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: s.atual ? "var(--accent)" : "var(--ink-3)" }}>
                      {s.atual ? "agora" : tempoRelativo(s.ultima_atividade_em)}
                    </span>
                    {!s.atual ? (
                      <button className="btn btn--ghost btn--sm" type="button" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => encerrarUma(s.id)}>Encerrar</button>
                    ) : null}
                  </div>
                ))
              )}
            </div>
            {temOutras ? (
              <AsyncButton variant="secondary" onClick={encerrarOutras} successMessage="Outras sessões encerradas." style={{ alignSelf: "flex-start" }}>
                Encerrar outras sessões
              </AsyncButton>
            ) : null}
          </div>
        </div>
      </div>

      {/* Painel de enrollment MFA */}
      <MfaEnrollmentPanel open={mfaPanelOpen} onClose={() => setMfaPanelOpen(false)} onCompleted={carregarMfa} />

      {/* Desativar MFA */}
      <SlideOverPanel
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Desativar MFA"
        subtitle="Confirme sua senha e um código do app para desativar."
      >
        <form onSubmit={handleDisable} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="input-group">
            <label className="input-label">Senha</label>
            <input className="input" type="password" value={disableForm.senha} onChange={(e) => setDisableForm((f) => ({ ...f, senha: e.target.value }))} required />
          </div>
          <div className="input-group">
            <label className="input-label">Código do autenticador</label>
            <input className="input mono" value={disableForm.codigo} onChange={(e) => setDisableForm((f) => ({ ...f, codigo: e.target.value }))} required />
          </div>
          {disableErr ? <div style={{ color: "var(--danger-text)", fontSize: 13 }}>{disableErr}</div> : null}
          <button className="btn btn--danger" type="submit">Desativar MFA</button>
        </form>
      </SlideOverPanel>

      {/* Gerar novos códigos */}
      <SlideOverPanel
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        title="Gerar novos códigos de recuperação"
        subtitle="Os códigos antigos deixam de funcionar."
      >
        {novosCodigos ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: "var(--ink-2)" }}>Guarde estes códigos em um local seguro:</div>
            <div className="mono" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "var(--surface-2)", padding: 14, borderRadius: "var(--radius-md)", fontSize: 13 }}>
              {novosCodigos.map((c) => <span key={c}>{c}</span>)}
            </div>
            <button className="btn btn--primary" type="button" onClick={() => setRegenOpen(false)}>Concluir</button>
          </div>
        ) : (
          <form onSubmit={handleRegen} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="input-group">
              <label className="input-label">Código do autenticador</label>
              <input className="input mono" value={regenCodigo} onChange={(e) => setRegenCodigo(e.target.value)} required />
            </div>
            {regenErr ? <div style={{ color: "var(--danger-text)", fontSize: 13 }}>{regenErr}</div> : null}
            <button className="btn btn--primary" type="submit">Gerar novos códigos</button>
          </form>
        )}
      </SlideOverPanel>
    </section>
  );
}
